import { useState, type ChangeEvent, type FormEvent } from 'react';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/daikn0roh653ivfvofb0';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const focusOptions = [
  { value: '热点事件', label: '热点事件' },
  { value: '综艺动态', label: '综艺动态' },
  { value: '影视风向', label: '影视风向' },
  { value: '音乐现场', label: '音乐现场' },
  { value: '网络话题', label: '网络话题' },
];

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageLength(event.target.value.length);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete('contact_alt');

    setStatus('sending');
    setFormError('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          payload.append(key, value);
        }
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');
      const isSuccess = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isSuccess) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(
          serverMessage ? String(serverMessage) : '提交未成功，请稍后重试，或直接发送邮件联系我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试，或直接发送邮件联系我们。');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="sub-nickname" className="text-sm font-medium text-foreground-800">
            昵称
          </label>
          <input
            id="sub-nickname"
            name="nickname"
            type="text"
            placeholder="怎么称呼你"
            className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="sub-email" className="text-sm font-medium text-foreground-800">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="sub-email"
            name="email"
            type="email"
            required
            placeholder="用于接收更新提醒"
            className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <label htmlFor="sub-focus" className="text-sm font-medium text-foreground-800">
            最想跟进的方向
          </label>
          <select
            id="sub-focus"
            name="focus"
            defaultValue={focusOptions[0].value}
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            {focusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <label htmlFor="sub-message" className="text-sm font-medium text-foreground-800">
            想补充的线索或留言
          </label>
          <textarea
            id="sub-message"
            name="message"
            rows={4}
            maxLength={500}
            onChange={handleMessageChange}
            placeholder="可以附上你认为值得跟进的公开来源线索，最多 500 字"
            className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          ></textarea>
          <span className="self-end text-xs text-foreground-400">{messageLength} / 500</span>
        </div>
      </div>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-verify-aux"
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'sending'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'sending' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
          {status === 'sending' ? '正在提交…' : '提交并订阅更新'}
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你愿意接收更新提醒，我们不会将邮箱用于其他用途。
        </p>
      </div>

      {status === 'success' && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
          <span>已收到，感谢你的关注与补充，我们会在核对来源后跟进。</span>
        </div>
      )}

      {status === 'error' && formError && (
        <div className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          <span>{formError}</span>
        </div>
      )}
    </form>
  );
}